import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { useQuery } from "@tanstack/react-query";
import { actionItemsService } from "../../services/supabase/action-items";
import { startOfToday, endOfToday, startOfWeek, endOfWeek, isAfter, isBefore, isSameDay } from "date-fns";
import { ActionItem } from "../../types/actionItems";
import { ArrowRight } from "lucide-react";
import { TaskCategories } from "./TaskCategories";
import React from "react";

export function DashboardActionItemsList() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: actionItems = [], isLoading } = useQuery({
    queryKey: ['actionItems', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      return actionItemsService.getActionItems(user.id);
    },
    enabled: !!user?.id
  });

  const today = new Date();
  const startOfCurrentWeek = startOfWeek(today, { weekStartsOn: 1 });
  const endOfCurrentWeek = endOfWeek(today, { weekStartsOn: 1 });

  const categorizedTasks = actionItems.reduce((acc: {
    overdue: ActionItem[];
    today: ActionItem[];
    thisWeek: ActionItem[];
    upcoming: ActionItem[];
  }, item) => {
    if (item.status === 'done' || !item.due_date) return acc;
    
    const dueDate = new Date(item.due_date);
    const isOverdue = isBefore(dueDate, startOfToday());
    const isDueToday = isSameDay(dueDate, today);
    const isDueThisWeek = !isDueToday && 
      isAfter(dueDate, startOfCurrentWeek) && 
      isBefore(dueDate, endOfCurrentWeek);
    const isUpcoming = isAfter(dueDate, endOfCurrentWeek);

    if (isOverdue) {
      acc.overdue.push(item);
    } else if (isDueToday) {
      acc.today.push(item);
    } else if (isDueThisWeek) {
      acc.thisWeek.push(item);
    } else if (isUpcoming) {
      acc.upcoming.push(item);
    }

    return acc;
  }, {
    overdue: [],
    today: [],
    thisWeek: [],
    upcoming: []
  });

  const shouldShowUpcoming = () => {
    const visibleCategories = [
      categorizedTasks.overdue,
      categorizedTasks.today,
      categorizedTasks.thisWeek
    ].filter(category => category.length > 0).length;
    
    return visibleCategories <= 2;
  };

  const handleViewAll = () => {
    navigate('/app/action-items');
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((n) => (
          <div key={n} className="h-24 bg-muted animate-pulse rounded-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden border-2 border-slate-300 dark:border-slate-700 rounded-3xl bg-slate-200 dark:bg-slate-700/50 p-4">
      <div className="relative z-10">
        <h2 className="text-lg font-bold mb-4 md:mb-4 px-2 text-foreground">Action Items</h2>
        
        <TaskCategories 
          categorizedTasks={categorizedTasks}
          showUpcoming={shouldShowUpcoming()}
        />

        {actionItems.length > 0 ? (
          <div className="mt-6 flex justify-end px-4">
            <Button
              onClick={handleViewAll}
              className="button button-default"
            >
              View all action items
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            No action items to display
          </div>
        )}
      </div>
    </div>
  );
}
